/**
 * Primitives partagées pour les panels — Lot 2 du plan UI/UX.
 * Objectif : un seul composant par concept (détail latéral, KPI, badge,
 * toolbar) pour qu'on arrête de redéfinir le même HTML/CSS dans chaque
 * panel. Les classes CSS sous-jacentes (.panel-detail-*, .panel-badge--*)
 * existent déjà dans public/operator.css — ces composants en sont des
 * wrappers typés.
 *
 * Convention d'intention partagée : "ok" | "warn" | "danger" | "info" | "muted".
 */
import { For, Show, type JSX, type Component, type Accessor } from "solid-js";
import type { RefreshState } from "./use-panel-refresh";
import "./panel-primitives.css";

export type Intent = "ok" | "warn" | "danger" | "info" | "muted";

// ─── PanelBadge ───────────────────────────────────────────────────────────
// Remplace `<span class="panel-badge panel-badge--ok">label</span>`.

export interface PanelBadgeProps {
  intent?: Intent;
  title?: string;
  children: JSX.Element;
}

export const PanelBadge: Component<PanelBadgeProps> = (props) => {
  const intent = () => props.intent ?? "muted";
  return (
    <span class={`panel-badge panel-badge--${intent()}`} title={props.title}>
      {props.children}
    </span>
  );
};

// ─── PanelKpi / PanelKpiGrid ──────────────────────────────────────────────
// Cards de stats au-dessus d'un tableau. Reprend le pattern services.css
// (.svc-kpi*) en générique. Grille auto-fit minmax(150px, 1fr).

export interface PanelKpiProps {
  label: string;
  value: JSX.Element;
  /** Sous-texte optionnel (ex: "cumul VMs en marche", "3 demarrees"). */
  sub?: JSX.Element;
  /** Slot libre rendu à gauche (donut SVG, icône, mini-graph). */
  leading?: JSX.Element;
  intent?: Intent;
  title?: string;
}

export const PanelKpi: Component<PanelKpiProps> = (props) => {
  const intent = () => props.intent ?? "muted";
  return (
    <div class={`panel-kpi panel-kpi--${intent()}`} title={props.title}>
      <Show when={props.leading}>
        <div class="panel-kpi__leading">{props.leading}</div>
      </Show>
      <div class="panel-kpi__body">
        <span class="panel-kpi__label">{props.label}</span>
        <span class="panel-kpi__value">{props.value}</span>
        <Show when={props.sub}>
          <span class="panel-kpi__sub">{props.sub}</span>
        </Show>
      </div>
    </div>
  );
};

export const PanelKpiGrid: Component<{ children: JSX.Element }> = (props) => (
  <div class="panel-kpis">{props.children}</div>
);

// ─── RefreshIndicator ────────────────────────────────────────────────────
// Petit témoin "live / polling / stale / error" alimenté par use-panel-refresh.
// Se place typiquement à droite du bouton ↺ dans la toolbar du panel.

const REFRESH_LABEL: Record<RefreshState["mode"], string> = {
  live: "live",
  polling: "actualisation…",
  manual: "à jour",
  stale: "périmé",
  error: "erreur",
};

export interface RefreshIndicatorProps {
  state: Accessor<RefreshState>;
}

export const RefreshIndicator: Component<RefreshIndicatorProps> = (props) => {
  const mode = () => props.state().mode;
  // Réutilise la classe globale .pulse-dot (tokens.css) sur les modes
  // actifs uniquement — pas d'animation pour stale/manual/error.
  const dotClass = () => {
    const animated = mode() === "live" || mode() === "polling";
    return animated ? "panel-refresh__dot pulse-dot" : "panel-refresh__dot";
  };
  const title = () => {
    const ts = props.state().lastUpdate;
    if (!ts) return REFRESH_LABEL[mode()];
    const dt = Math.round((Date.now() - ts) / 1000);
    return `${REFRESH_LABEL[mode()]} — màj il y a ${dt}s`;
  };
  return (
    <span class={`panel-refresh panel-refresh--${mode()}`} title={title()} aria-live="polite">
      <span class={dotClass()} aria-hidden="true" />
      <span class="panel-refresh__label">{REFRESH_LABEL[mode()]}</span>
    </span>
  );
};

// ─── PanelToolbar ────────────────────────────────────────────────────────
// Filtre + actions à droite. Évite que chaque panel redéfinisse son flexbox
// d'en-tête. `filter` est un slot libre (typiquement <input class="panel-filter-input"/>)
// et `actions` est rendu aligné à droite.

export interface PanelToolbarProps {
  filter?: JSX.Element;
  actions?: JSX.Element;
  children?: JSX.Element;
}

export const PanelToolbar: Component<PanelToolbarProps> = (props) => (
  <div class="panel-prim-toolbar">
    <Show when={props.filter}>
      <div class="panel-prim-toolbar__filter">{props.filter}</div>
    </Show>
    {props.children}
    <Show when={props.actions}>
      <div class="panel-prim-toolbar__actions">{props.actions}</div>
    </Show>
  </div>
);

// ─── PanelDetail ─────────────────────────────────────────────────────────
// Drawer latéral droit (slidein). Sur < 900 px, le drawer passe en plein
// écran overlay (cf. panel-primitives.css). Réutilise les classes
// panel-detail-* existantes pour rester compatible avec les panels actuels
// pendant la migration.

export interface DetailSection {
  /** Titre court ; passé en eyebrow (uppercase). */
  label: string;
  /** Pour les sections key/value, un dl auto-rendu. */
  items?: Array<[label: string, value: JSX.Element]>;
  /** Contenu libre, prioritaire sur `items`. */
  children?: JSX.Element;
}

export interface PanelDetailProps {
  title: JSX.Element;
  onClose: () => void;
  /** Boutons d'action (Démarrer, Arrêter, …) — affichés sous le header. */
  actions?: JSX.Element;
  /** Sections structurées en eyebrow + dl ou contenu libre. */
  sections?: DetailSection[];
  /** Contenu libre alternatif (si pas de `sections`). */
  children?: JSX.Element;
}

// ─── PanelPreview ────────────────────────────────────────────────────────
// État vide standard pour un panel dont le plugin agent n'est pas encore
// livré. Affiche le contrat attendu (feature gate, liste des outils) pour
// que celui qui implémentera côté agent sache exactement quoi exposer.

export interface PanelPreviewTool {
  name: string;
  description: string;
}

export interface PanelPreviewProps {
  /** Nom de feature attendue dans `ws.state.agentFeatures.<feature>`. */
  feature: string;
  /** Phrase courte décrivant le but du panel. */
  goal: string;
  /** Outils que l'agent devra exposer. */
  tools: PanelPreviewTool[];
  /** Optionnel : précisions techniques (binaire requis, droits, etc.). */
  notes?: string;
}

export const PanelPreview: Component<PanelPreviewProps> = (props) => {
  return (
    <div class="panel-preview" role="status">
      <div class="panel-preview__head">
        <span class="panel-preview__badge">Aperçu</span>
        <span class="panel-preview__title">Plugin à venir</span>
      </div>
      <p class="panel-preview__goal">{props.goal}</p>

      <div class="panel-preview__section">
        <div class="panel-preview__section-label">Feature gate attendue</div>
        <code class="panel-preview__code">ws.state.agentFeatures.{props.feature} === true</code>
      </div>

      <div class="panel-preview__section">
        <div class="panel-preview__section-label">Outils exposés par l'agent</div>
        <ul class="panel-preview__tools">
          <For each={props.tools}>
            {(t) => (
              <li>
                <code>{t.name}</code> — <span>{t.description}</span>
              </li>
            )}
          </For>
        </ul>
      </div>

      <Show when={props.notes}>
        <div class="panel-preview__section">
          <div class="panel-preview__section-label">Notes</div>
          <p class="panel-preview__notes">{props.notes}</p>
        </div>
      </Show>
    </div>
  );
};

export const PanelDetail: Component<PanelDetailProps> = (props) => {
  return (
    <aside class="panel-detail-slidein panel-detail-slidein--responsive" role="complementary">
      <div class="panel-detail-header">
        <h3>{props.title}</h3>
        <button
          type="button"
          class="panel-detail-close"
          onClick={() => props.onClose()}
          aria-label="Fermer le détail"
          title="Fermer"
        >
          ✕
        </button>
      </div>

      <Show when={props.actions}>
        <div class="panel-detail-actions">{props.actions}</div>
      </Show>

      <Show
        when={props.sections && props.sections.length > 0}
        fallback={<Show when={props.children}>{props.children}</Show>}
      >
        <For each={props.sections}>
          {(section) => (
            <div class="panel-detail-section">
              <h4>{section.label}</h4>
              <Show
                when={section.items && section.items.length > 0}
                fallback={<Show when={section.children}>{section.children}</Show>}
              >
                <dl class="panel-detail-dl">
                  <For each={section.items}>
                    {([k, v]) => (
                      <>
                        <dt>{k}</dt>
                        <dd>{v}</dd>
                      </>
                    )}
                  </For>
                </dl>
              </Show>
            </div>
          )}
        </For>
      </Show>
    </aside>
  );
};
